import { useState, useMemo } from 'react';
import {
  Plus,
  Loader2,
  ShieldCheck,
  Pencil,
  Trash2,
  Star,
} from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  useCancellationPolicies,
  useCreateCancellationPolicy,
  useUpdateCancellationPolicy,
  useDeleteCancellationPolicy,
} from '@/hooks/api/useRevenue';
import type {
  CancellationPolicy,
  CancellationRule,
  CreateCancellationPolicyPayload,
  UpdateCancellationPolicyPayload,
} from '@/services/api/revenue.api';

// ─── Page ────────────────────────────────────────────────────────────────────

export function CancellationPoliciesPage() {
  const propertyId = useSelectedPropertyId();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CancellationPolicy | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: policies, isLoading } = useCancellationPolicies({ propertyId });
  const deleteMutation = useDeleteCancellationPolicy();

  const allItems: CancellationPolicy[] = policies?.data ?? [];

  const items: CancellationPolicy[] = useMemo(() => {
    if (!searchTerm) return allItems;
    const q = searchTerm.toLowerCase();
    return allItems.filter(
      (p) => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
    );
  }, [allItems, searchTerm]);

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this cancellation policy?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div>
      <PageHeader
        title="Cancellation Policies"
        description="Manage cancellation policies for your properties"
        actions={
          <Button onClick={() => { setEditing(null); setShowForm(true); }}>
            <Plus size={16} aria-hidden="true" />
            Add Policy
          </Button>
        }
      />

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search cancellation policies"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No cancellation policies" description="Create your first cancellation policy." actionLabel="Add Policy" onAction={() => setShowForm(true)} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Default</Th>
                  <Th>Description</Th>
                  <Th>Rules</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((policy) => (
                  <tr key={policy.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{policy.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{policy.code}</td>
                    <td className="px-4 py-3">
                      {policy.isDefault && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700">
                          <Star size={12} aria-hidden="true" />
                          Default
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">{policy.description ?? '--'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{policy.rules.length} rule(s)</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => { setEditing(policy); setShowForm(true); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Edit">
                          <Pencil size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(policy.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing ? 'Edit Cancellation Policy' : 'Add Cancellation Policy'} width="max-w-xl">
        <CancellationPolicyForm propertyId={propertyId} editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} />
      </SlideOver>
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function CancellationPolicyForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: CancellationPolicy | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [isDefault, setIsDefault] = useState(editing?.isDefault ?? false);
  const [rules, setRules] = useState<CancellationRule[]>(editing?.rules ?? []);

  const createMutation = useCreateCancellationPolicy();
  const updateMutation = useUpdateCancellationPolicy();

  const addRule = () => {
    setRules((prev) => [...prev, { daysBeforeArrival: 7, penaltyPercentage: 0, description: '' }]);
  };

  const updateRule = (index: number, field: keyof CancellationRule, value: string | number) => {
    setRules((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    );
  };

  const removeRule = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      const payload: UpdateCancellationPolicyPayload = { name, code, description: description || undefined, isDefault, rules };
      updateMutation.mutate({ id: editing.id, payload }, { onSuccess: () => onClose() });
    } else {
      const payload: CreateCancellationPolicyPayload = { propertyId, name, code, description: description || undefined, isDefault, rules };
      createMutation.mutate(payload, { onSuccess: () => onClose() });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="cp-name" className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
        <input id="cp-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="cp-code" className="block text-sm font-medium text-gray-700 mb-1">Code <span className="text-red-500">*</span></label>
        <input id="cp-code" type="text" required maxLength={20} value={code} onChange={(e) => setCode(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="cp-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea id="cp-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="flex items-center gap-2">
        <input id="cp-default" type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="cp-default" className="text-sm font-medium text-gray-700">Set as default</label>
      </div>

      {/* Rules */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700">Cancellation Rules</h4>
          <Button type="button" size="sm" variant="secondary" onClick={addRule}>
            <Plus size={14} aria-hidden="true" />
            Add Rule
          </Button>
        </div>

        {rules.length === 0 ? (
          <p className="text-sm text-gray-400">No rules defined. Add a rule to specify cancellation penalties.</p>
        ) : (
          <div className="space-y-3">
            {rules.map((rule, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">Rule {i + 1}</span>
                  <button type="button" onClick={() => removeRule(i)} className="text-gray-400 hover:text-red-500" aria-label="Remove rule">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Days before arrival</label>
                    <input type="number" min={0} value={rule.daysBeforeArrival} onChange={(e) => updateRule(i, 'daysBeforeArrival', Number(e.target.value))} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Penalty %</label>
                    <input type="number" min={0} max={100} value={rule.penaltyPercentage} onChange={(e) => updateRule(i, 'penaltyPercentage', Number(e.target.value))} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Description (optional)</label>
                    <input type="text" value={rule.description ?? ''} onChange={(e) => updateRule(i, 'description', e.target.value)} placeholder="e.g. Free cancellation up to 7 days before arrival" className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>{editing ? 'Update' : 'Create'}</Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
